import { useMemo, useRef, useState } from "react";
import { useSessions, type SurfSession } from "@/hooks/use-sessions";
import { parseTrackFile, type ParsedTrack } from "@/lib/parseTrack";
import { surfbreaks } from "@/data/surfbreaks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Loader2, Plus, Waves, Globe, Lock, Trash2, Upload, Activity, Gauge, FileUp,
} from "lucide-react";

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });

function SessionCard({
  s, own, onToggleVisibility, onDelete,
}: {
  s: SurfSession;
  own: boolean;
  onToggleVisibility?: (id: string, v: "public" | "private") => void;
  onDelete?: (id: string) => void;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card/80 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-display text-lg truncate">{s.title}</h4>
            {s.source === "upload" && (
              <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
                <Activity className="h-3 w-3" /> GPS track
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {fmtDate(s.started_at)}{s.spot_name ? ` · ${s.spot_name}` : ""}
          </p>
        </div>
        {own && (
          <button
            onClick={() => onDelete?.(s.id)}
            className="p-1.5 rounded-full hover:bg-destructive/10 hover:text-destructive shrink-0"
            aria-label="Delete session"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm">
        {s.duration_minutes != null && (
          <span className="text-muted-foreground">{s.duration_minutes} min</span>
        )}
        {s.distance_km != null && (
          <span className="text-muted-foreground">{s.distance_km.toFixed(1)} km</span>
        )}
        {s.avg_speed_kmh != null && (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Gauge className="h-3.5 w-3.5" /> {s.avg_speed_kmh.toFixed(1)} km/h avg
          </span>
        )}
      </div>

      {s.notes && <p className="text-sm mt-2 text-foreground/90">{s.notes}</p>}

      {own && onToggleVisibility && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
          {s.visibility === "public"
            ? <Globe className="h-3.5 w-3.5 text-primary" />
            : <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
          <span className="text-xs text-muted-foreground flex-1">
            {s.visibility === "public" ? "Shared publicly" : "Private — only you"}
          </span>
          <Switch
            checked={s.visibility === "public"}
            onCheckedChange={(checked) => onToggleVisibility(s.id, checked ? "public" : "private")}
          />
        </div>
      )}
    </div>
  );
}

export const SessionLog = () => {
  const { mine, feed, loading, logSession, logUploadedSession, setVisibility, deleteSession } = useSessions();
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<"mine" | "feed">("mine");
  const fileRef = useRef<HTMLInputElement>(null);

  // Pending upload: parsed track waiting for a title/spot/visibility before saving
  const [pendingTrack, setPendingTrack] = useState<ParsedTrack | null>(null);
  const [parsing, setParsing] = useState(false);

  // Shared form state (used for both manual entry and confirming an upload)
  const [title, setTitle] = useState("");
  const [spotId, setSpotId] = useState<string>("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [duration, setDuration] = useState("60");
  const [notes, setNotes] = useState("");
  const [visibility, setVisibility_] = useState<"public" | "private">("private");

  const selectedSpot = useMemo(() => surfbreaks.find((b) => b.id === spotId), [spotId]);

  const resetForm = () => {
    setTitle(""); setSpotId(""); setDate(new Date().toISOString().slice(0, 10));
    setDuration("60"); setNotes(""); setVisibility_("private");
    setPendingTrack(null);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setParsing(true);
    try {
      const track = await parseTrackFile(file);
      setPendingTrack(track);
      setTitle(file.name.replace(/\.(gpx|tcx)$/i, ""));
      setDate(track.startedAt.slice(0, 10));
      setFormOpen(true);
      toast.success(`Parsed a ${track.distanceKm.toFixed(1)} km session`);
    } catch (err: any) {
      toast.error(err.message ?? "Couldn't read that file");
    } finally {
      setParsing(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) { toast.error("Give the session a title"); return; }
    setSaving(true);
    try {
      if (pendingTrack) {
        await logUploadedSession({
          title: title.trim(),
          spot_name: selectedSpot?.name ?? null,
          notes: notes.trim() || null,
          visibility,
          track: pendingTrack,
        });
      } else {
        await logSession({
          title: title.trim(),
          spot_name: selectedSpot?.name ?? null,
          lat: selectedSpot?.lat ?? null,
          lon: selectedSpot?.lon ?? null,
          started_at: new Date(date).toISOString(),
          duration_minutes: duration ? parseInt(duration, 10) : null,
          notes: notes.trim() || null,
          visibility,
        });
      }
      toast.success("Session logged");
      resetForm();
      setFormOpen(false);
    } catch (err: any) {
      toast.error(err.message ?? "Couldn't save session");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload a track file */}
      <div className="rounded-2xl border border-border bg-card/80 p-4 flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <FileUp className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">Upload a session file</p>
          <p className="text-xs text-muted-foreground">
            .gpx or .tcx — export it from your watch's app (Garmin Connect, Coros, Suunto, etc). Free, no account linking needed.
          </p>
        </div>
        <input ref={fileRef} type="file" accept=".gpx,.tcx" className="hidden" onChange={handleFileSelect} />
        <Button size="sm" onClick={() => fileRef.current?.click()} disabled={parsing}>
          {parsing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          <span className="ml-1.5">Upload</span>
        </Button>
      </div>

      {/* Log manually */}
      {!formOpen ? (
        <Button onClick={() => setFormOpen(true)} variant="outline" className="w-full">
          <Plus className="h-4 w-4 mr-2" /> Log a session manually
        </Button>
      ) : (
        <div className="rounded-2xl border border-border bg-card/80 p-4 space-y-3">
          {pendingTrack && (
            <div className="rounded-xl bg-primary/5 border border-primary/20 px-3 py-2 text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
              <span>{pendingTrack.distanceKm.toFixed(1)} km</span>
              <span>{pendingTrack.durationMinutes} min</span>
              <span>{pendingTrack.avgSpeedKmh.toFixed(1)} km/h avg</span>
            </div>
          )}
          <Input placeholder="Session title (e.g. Evening glass-off)" value={title} onChange={(e) => setTitle(e.target.value)} />
          <Select value={spotId} onValueChange={setSpotId}>
            <SelectTrigger><SelectValue placeholder="Spot (optional)" /></SelectTrigger>
            <SelectContent>
              {surfbreaks.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!pendingTrack && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Date</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Duration (min)</Label>
                <Input type="number" min="0" value={duration} onChange={(e) => setDuration(e.target.value)} />
              </div>
            </div>
          )}
          <Textarea placeholder="Notes — conditions, waves, gear…" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="flex items-center gap-2">
            {visibility === "public" ? <Globe className="h-3.5 w-3.5 text-primary" /> : <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
            <span className="text-xs text-muted-foreground flex-1">Share this session publicly</span>
            <Switch checked={visibility === "public"} onCheckedChange={(c) => setVisibility_(c ? "public" : "private")} />
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-gradient-sunset text-accent-foreground">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save session"}
            </Button>
            <Button variant="ghost" onClick={() => { setFormOpen(false); resetForm(); }}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Mine / Feed toggle */}
      <div className="flex gap-1 rounded-full bg-card/80 border border-border p-1">
        <button
          onClick={() => setTab("mine")}
          className={`flex-1 rounded-full py-2 text-xs sm:text-sm font-medium transition-colors ${tab === "mine" ? "bg-gradient-sunset text-accent-foreground" : "text-muted-foreground"}`}
        >
          My log ({mine.length})
        </button>
        <button
          onClick={() => setTab("feed")}
          className={`flex-1 rounded-full py-2 text-xs sm:text-sm font-medium transition-colors ${tab === "feed" ? "bg-gradient-sunset text-accent-foreground" : "text-muted-foreground"}`}
        >
          Community feed
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : tab === "mine" ? (
        mine.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No sessions logged yet.</p>
        ) : (
          <div className="space-y-3">
            {mine.map((s) => (
              <SessionCard key={s.id} s={s} own onToggleVisibility={setVisibility} onDelete={deleteSession} />
            ))}
          </div>
        )
      ) : feed.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8 flex flex-col items-center gap-2">
          <Waves className="h-5 w-5" /> No public sessions yet — be the first to share one.
        </p>
      ) : (
        <div className="space-y-3">
          {feed.map((s) => (
            <div key={s.id}>
              <p className="text-xs text-muted-foreground mb-1 ml-1">{s.author_name}</p>
              <SessionCard s={s} own={false} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
